/**
 * CREATIVE DETONATOR - User Error Handler
 * Converts technical errors to user-friendly messages
 *
 * @version 1.0.0
 * @description Implements FRONTEND-24 (User Error Feedback)
 */

// ============================================================================
// ERROR TYPES
// ============================================================================

export const ErrorTypes = {
  NETWORK: 'network',
  AUTH: 'auth',
  VALIDATION: 'validation',
  SERVER: 'server',
  CREDITS: 'credits',
  TIMEOUT: 'timeout',
  RATE_LIMIT: 'rate_limit',
  NOT_FOUND: 'not_found',
  UNKNOWN: 'unknown'
};

// ============================================================================
// USER-FRIENDLY MESSAGES
// ============================================================================

const ErrorMessages = {
  [ErrorTypes.NETWORK]: {
    title: 'Connection Error',
    message: 'Unable to connect to the server. Check your internet connection.',
    action: 'Try Again',
    recoverable: true
  },

  [ErrorTypes.AUTH]: {
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.',
    action: 'Log In',
    recoverable: true
  },

  [ErrorTypes.VALIDATION]: {
    title: 'Invalid Input',
    message: 'Please check your input and try again.',
    action: 'Fix & Retry',
    recoverable: true
  },

  [ErrorTypes.SERVER]: {
    title: 'Server Error',
    message: 'Something went wrong on our end. We\'ve been notified.',
    action: 'Try Again',
    recoverable: true
  },

  [ErrorTypes.CREDITS]: {
    title: 'Insufficient Credits',
    message: 'You don\'t have enough credits for this action.',
    action: 'Get Credits',
    recoverable: false
  },

  [ErrorTypes.TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long. Please try again.',
    action: 'Try Again',
    recoverable: true
  },

  [ErrorTypes.RATE_LIMIT]: {
    title: 'Too Many Requests',
    message: 'Please wait a moment before trying again.',
    action: 'Wait',
    recoverable: true
  },

  [ErrorTypes.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    action: 'Go Back',
    recoverable: false
  },

  [ErrorTypes.UNKNOWN]: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred.',
    action: 'Dismiss',
    recoverable: false
  }
};

// ============================================================================
// ERROR CLASSIFICATION
// ============================================================================

/**
 * Classify error type from error object or response
 * @param {Error} error - Error object
 * @param {Object} response - Optional response data
 * @returns {string} Error type
 */
export function classifyError(error, response = null) {
  // Network errors
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return ErrorTypes.NETWORK;
  }

  if (error.name === 'AbortError') {
    return ErrorTypes.TIMEOUT;
  }

  if (error.message?.toLowerCase().includes('timeout')) {
    return ErrorTypes.TIMEOUT;
  }

  if (error.message?.toLowerCase().includes('network')) {
    return ErrorTypes.NETWORK;
  }

  // HTTP status-based classification
  if (response?.status) {
    switch (response.status) {
      case 401:
        return ErrorTypes.AUTH;
      case 403:
        if (response.data?.code === 'CREDITS_INSUFFICIENT') {
          return ErrorTypes.CREDITS;
        }
        return ErrorTypes.AUTH;
      case 400:
      case 422:
        return ErrorTypes.VALIDATION;
      case 404:
        return ErrorTypes.NOT_FOUND;
      case 429:
        return ErrorTypes.RATE_LIMIT;
      case 408:
      case 504:
        return ErrorTypes.TIMEOUT;
      case 500:
      case 502:
      case 503:
        return ErrorTypes.SERVER;
    }
  }

  // Error code classification
  if (error.code) {
    switch (error.code) {
      case 'AUTH_MISSING':
      case 'AUTH_INVALID':
      case 'AUTH_EXPIRED':
        return ErrorTypes.AUTH;
      case 'CREDITS_INSUFFICIENT':
        return ErrorTypes.CREDITS;
      case 'VALIDATION_ERROR':
        return ErrorTypes.VALIDATION;
      case 'RATE_LIMIT':
        return ErrorTypes.RATE_LIMIT;
    }
  }

  return ErrorTypes.UNKNOWN;
}

// ============================================================================
// ERROR DISPLAY
// ============================================================================

/**
 * Show error to user via toast notification
 * @param {Error|string} error - Error object or message
 * @param {Object} options - Display options
 * @returns {Object} Error info
 */
export function showError(error, options = {}) {
  const {
    context = null,       // What action failed (e.g., "saving idea")
    toast = true,         // Show toast notification
    modal = false,        // Show modal dialog
    onRetry = null,       // Retry callback
    onDismiss = null,     // Dismiss callback
    duration = 5000,      // Toast duration
    response = null       // HTTP response for classification
  } = options;

  // Handle string errors
  if (typeof error === 'string') {
    error = new Error(error);
  }

  // Classify error
  const errorType = classifyError(error, response);
  const errorInfo = ErrorMessages[errorType] || ErrorMessages[ErrorTypes.UNKNOWN];

  // Build message
  let title = errorInfo.title;
  let message = errorInfo.message;

  // Add context
  if (context) {
    title = `Error ${context}`;
  }

  // Add validation details
  if (errorType === ErrorTypes.VALIDATION && error.details) {
    const details = Array.isArray(error.details)
      ? error.details.map(d => d.message || d).join('. ')
      : error.details;
    message = details || message;
  }

  // Add server error message if available
  if (error.serverMessage) {
    message = error.serverMessage;
  }

  // Log for debugging (in development)
  console.error(`[Error] ${title}:`, error);

  // Show toast using existing Toast system if available
  if (toast && window.Toast) {
    window.Toast.error(message, {
      title,
      duration,
      action: errorInfo.recoverable && onRetry ? {
        label: errorInfo.action,
        callback: onRetry
      } : null
    });
  } else if (toast) {
    // Fallback to simple alert or console
    console.error(`${title}: ${message}`);
  }

  // Show modal for critical errors
  if (modal && window.ModalFactory) {
    // Use ModalFactory if available
    const { createModal } = window.ModalFactory;
    const errorModal = createModal({
      id: 'error-modal',
      title: title,
      content: `
        <div class="error-modal-content">
          <p>${message}</p>
          ${errorInfo.recoverable && onRetry ? `
            <button class="btn btn-primary" data-action="retry">${errorInfo.action}</button>
          ` : ''}
          <button class="btn" data-action="dismiss">Dismiss</button>
        </div>
      `,
      onOpen: (modalEl) => {
        const retryBtn = modalEl.querySelector('[data-action="retry"]');
        const dismissBtn = modalEl.querySelector('[data-action="dismiss"]');

        if (retryBtn && onRetry) {
          retryBtn.addEventListener('click', () => {
            errorModal.close();
            onRetry();
          });
        }
        if (dismissBtn) {
          dismissBtn.addEventListener('click', () => {
            errorModal.close();
            if (onDismiss) onDismiss();
          });
        }
      }
    });
    errorModal.open();
  }

  return { errorType, errorInfo, error };
}

// ============================================================================
// API ERROR HANDLER
// ============================================================================

/**
 * Handle API response errors
 * @param {Response} response - Fetch response
 * @param {Object} options - Error options
 */
export async function handleApiError(response, options = {}) {
  let data = {};

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : {};
  } catch (e) {
    // Response not JSON
  }

  const error = new Error(data.error || data.message || response.statusText);
  error.code = data.code;
  error.details = data.details;
  error.serverMessage = data.error || data.message;

  return showError(error, {
    ...options,
    response: { status: response.status, data }
  });
}

// ============================================================================
// WRAPPER FOR ASYNC OPERATIONS
// ============================================================================

/**
 * Wrap async function with error handling
 * @param {Function} fn - Async function to wrap
 * @param {Object} options - Error options
 * @returns {Function} Wrapped function
 */
export function withErrorHandling(fn, options = {}) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      showError(error, options);
      throw error; // Re-throw for caller handling if needed
    }
  };
}

/**
 * Safe async operation with loading state
 * @param {Function} fn - Async function to execute
 * @param {Object} options - Options
 * @returns {any} Result or null on error
 */
export async function safeAsync(fn, options = {}) {
  const {
    context,
    onSuccess,
    onError,
    showLoading = false,
    loadingMessage = 'Loading...',
    rethrow = false
  } = options;

  let loadingToast;

  try {
    if (showLoading && window.Toast?.loading) {
      loadingToast = window.Toast.loading(loadingMessage);
    }

    const result = await fn();

    if (loadingToast?.dismiss) {
      loadingToast.dismiss();
    }

    if (onSuccess) {
      onSuccess(result);
    }

    return result;

  } catch (error) {
    if (loadingToast?.dismiss) {
      loadingToast.dismiss();
    }

    showError(error, {
      context,
      onRetry: () => safeAsync(fn, options)
    });

    if (onError) {
      onError(error);
    }

    if (rethrow) {
      throw error;
    }

    return null;
  }
}

// ============================================================================
// SPECIFIC ERROR HANDLERS
// ============================================================================

/**
 * Handle generation errors
 * @param {Error} error
 */
export function handleGenerationError(error) {
  showError(error, {
    context: 'generating ideas',
    modal: true,
    onRetry: () => window.Generator?.retry?.()
  });
}

/**
 * Handle save errors
 * @param {Error} error
 * @param {string} ideaId
 */
export function handleSaveError(error, ideaId) {
  showError(error, {
    context: 'saving idea',
    toast: true,
    onRetry: () => window.IdeaActions?.save?.(ideaId)
  });
}

/**
 * Handle image generation errors
 * @param {Error} error
 * @param {string} ideaId
 */
export function handleImageError(error, ideaId) {
  showError(error, {
    context: 'generating image',
    toast: true,
    onRetry: () => window.ImageGen?.generate?.(ideaId)
  });
}

/**
 * Handle auth errors (redirect to login)
 * @param {Error} error
 */
export function handleAuthError(error) {
  showError(error, {
    context: 'authentication',
    toast: true,
    onRetry: () => {
      window.location.href = '/features/auth/pages/login.html';
    }
  });
}

// ============================================================================
// GLOBAL ERROR HANDLER
// ============================================================================

/**
 * Install global error handlers
 * Catches unhandled errors and promise rejections
 */
export function installGlobalHandler() {
  // Unhandled errors
  window.addEventListener('error', (event) => {
    console.error('[Global Error]', event.error);
    // Don't show toast for script errors - too noisy
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Unhandled Rejection]', event.reason);
    // Could show toast for API failures
    if (event.reason?.name === 'TypeError' && event.reason?.message?.includes('fetch')) {
      showError(event.reason, { context: 'network request' });
    }
  });
}

// ============================================================================
// GLOBAL EXPORT
// ============================================================================

window.ErrorHandler = {
  show: showError,
  handleApi: handleApiError,
  withHandling: withErrorHandling,
  safeAsync,
  handleGeneration: handleGenerationError,
  handleSave: handleSaveError,
  handleImage: handleImageError,
  handleAuth: handleAuthError,
  classify: classifyError,
  install: installGlobalHandler,
  ErrorTypes
};

export default {
  showError,
  handleApiError,
  withErrorHandling,
  safeAsync,
  handleGenerationError,
  handleSaveError,
  handleImageError,
  handleAuthError,
  classifyError,
  installGlobalHandler,
  ErrorTypes
};
